import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { validateFullUrlPath } from '@deltek/specification-client/core/Utilities/urlUtils'
import { Tooltip, withStyles } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import DeleteIcon from '@material-ui/icons/Delete'
import clsx from 'clsx'
import React from 'react'
import {
    SUSTAINABLE_MIND_BASE_URL,
    TRANSPARENCY_CATALOG_PLACEHOLDER,
} from '../ListingDetailsPage'
import { NormalizedListingDetailPartnershipLinkInput } from './EditPartnerships'

interface EditPartnershipTypeStyleProps {
    valid?: boolean
}

const HtmlTooltip = withStyles((theme: Theme) => ({
    tooltip: {
        backgroundColor: '#ffffff',
        color: '#242424',
        width: '100%',
        maxWidth: 'unset',
        outline: `1px solid #0098d6`,
        boxShadow: '4px 4px 5px rgba(0,0,0,0.3)',
        fontSize: pxToRem(12),
        textAlign: 'center',
    },
    arrow: {
        '&:before': {
            border: '1px solid #0098d6',
        },
        color: '#ffffff',
    },
    tooltipPlacementTop: {
        marginBottom: theme.spacing(0.6),
        marginTop: theme.spacing(0.6),
    },
}))(Tooltip)

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        height: (props: EditPartnershipTypeStyleProps) =>
            props.valid ? 60 : 72,
    },
    iconButton: {
        height: 36,
        width: 34,
        padding: 0,
        margin: 0,
        '& .MuiIconButton-label': {
            height: '100%',
        },
        '& .MuiIcon-root': {
            height: 'unset',
            width: 'unset',
        },
    },
    textFieldContainer: {
        paddingRight: theme.spacing(1),
    },
    textFieldInput: {
        paddingTop: theme.spacing(1.75),
        paddingBottom: theme.spacing(1.75),
        '&:placeholder-shown': {
            'text-overflow': 'ellipsis',
        },
    },
    errorWrapper: {
        marginTop: -22,
    },
}))

interface EditPartnershipTypeProps {
    detail: NormalizedListingDetailPartnershipLinkInput
    handleUpdateLink: (
        detail: NormalizedListingDetailPartnershipLinkInput
    ) => void
}

export const EditPartnershipType: React.FC<EditPartnershipTypeProps> = ({
    detail,
    handleUpdateLink,
}) => {
    const classes = useStyles({ valid: detail.url.valid })

    const debouncedInput = React.useCallback(
        (value: string) => {
            const debouncedValue =
                detail.name === 'Sustainable Minds'
                    ? value
                          .replace(`${SUSTAINABLE_MIND_BASE_URL}/company/`, '')
                          .toLowerCase()
                    : value.toLowerCase()

            const isValid =
                detail.name === 'Sustainable Minds'
                    ? validateFullUrlPath(
                          `${SUSTAINABLE_MIND_BASE_URL}/${debouncedValue}`
                      )
                    : validateFullUrlPath(value)

            handleUpdateLink({
                ...detail,
                url: {
                    ...detail.url,
                    value: debouncedValue,
                    valid: value === '' || isValid,
                },
            })
        },
        [handleUpdateLink, detail]
    )

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        debouncedInput(event.target.value)
    }

    const handleDelete = () => {
        handleUpdateLink({
            ...detail,
            url: {
                ...detail.url,
                value: '',
                valid: true,
            },
        })
    }

    return (
        <GridContainer direction="row" className={classes.container}>
            <GridContainer
                item
                xs={4}
                justifyContent="flex-start"
                alignItems="center"
                className={clsx(!detail.url.valid && classes.errorWrapper)}
            >
                <Typography component="span">{detail.name}</Typography>
            </GridContainer>
            <GridContainer
                item
                xs={7}
                alignItems="center"
                className={classes.textFieldContainer}
            >
                {detail.name === 'Sustainable Minds' ? (
                    <HtmlTooltip
                        placement="top"
                        interactive
                        arrow
                        title={
                            <Typography component="div">
                                {TRANSPARENCY_CATALOG_PLACEHOLDER}
                                <Typography component="div" />
                                <a
                                    href={SUSTAINABLE_MIND_BASE_URL}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    {SUSTAINABLE_MIND_BASE_URL}
                                </a>
                            </Typography>
                        }
                    >
                        <TextField
                            onChange={handleChange}
                            placeholder={TRANSPARENCY_CATALOG_PLACEHOLDER}
                            fullWidth
                            margin="dense"
                            variant="outlined"
                            value={detail.url.value?.replace(
                                `${SUSTAINABLE_MIND_BASE_URL}/company/`,
                                ''
                            )}
                            InputProps={{
                                classes: { input: classes.textFieldInput },
                            }}
                            size="small"
                            error={!detail.url.valid}
                            helperText={
                                !detail.url.valid
                                    ? 'Invalid Link. Please enter a valid URL.'
                                    : ''
                            }
                            autoComplete="off"
                        />
                    </HtmlTooltip>
                ) : (
                    <TextField
                        onChange={handleChange}
                        placeholder={'Add link here'}
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        value={detail.url.value}
                        InputProps={{
                            classes: { input: classes.textFieldInput },
                        }}
                        size="small"
                        error={!detail.url.valid}
                        helperText={
                            !detail.url.valid
                                ? 'Invalid Link. Please enter a valid URL.'
                                : ''
                        }
                        autoComplete="off"
                    />
                )}
            </GridContainer>
            <GridContainer
                item
                xs={1}
                justifyContent="flex-end"
                alignItems="center"
                className={clsx(!detail.url.valid && classes.errorWrapper)}
                data-testid="delete-button"
            >
                <IconButton
                    disabled={!detail.url.value}
                    className={classes.iconButton}
                    onClick={handleDelete}
                    aria-label="delete"
                >
                    <DeleteIcon color="primary" />
                </IconButton>
            </GridContainer>
        </GridContainer>
    )
}
